import { HardHat, Wrench, Users, Compass, Plus } from "lucide-react";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import VestGraphic from "@/components/VestGraphic";

const experiences = [
  { icon: HardHat, label: "Visit real construction projects" },
  { icon: Users, label: "Learn from builders & subcontractors" },
  { icon: Wrench, label: "Use tools when it's appropriate" },
  { icon: Compass, label: "Explore careers in the trades" },
];

export default function WhatItDoes() {
  return (
    <main data-testid="what-page">
      {/* HEADER */}
      <section className="bg-navy-ink text-white blueprint-grid pt-40 pb-24 md:pt-48 md:pb-32" data-testid="what-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand mb-6" data-testid="what-overline">What BuilderEDU Does</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.95] max-w-4xl" data-testid="what-headline">
              Learning that happens <span className="text-brand">on the job site.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-white py-24 md:py-32" data-testid="what-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <Reveal>
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed mb-8" data-testid="what-paragraph-1">
                BuilderEDU lets high school students experience the homebuilding process outside of a
                traditional classroom. Students visit real construction projects, learn directly from
                builders and subcontractors, and see how different trades — like framing, electrical,
                and plumbing — work together to build a home.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed" data-testid="what-paragraph-2">
                This isn't about watching from the sidelines. Whenever it's practical and safe, students
                get involved — using tools, asking questions, and learning what a career in construction
                actually looks like.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="md:col-span-5">
            <div className="bg-navy-ink text-white p-10 md:sticky md:top-32" data-testid="hands-on-callout">
              <div className="flex items-center gap-2.5 mb-5">
                <VestGraphic className="h-6 w-auto shrink-0" />
                <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand">The Big Idea</p>
              </div>
              <p className="font-display uppercase text-3xl md:text-4xl leading-tight">
                Hands-on whenever possible. <span className="text-brand">Not just watching.</span>
              </p>
            </div>
          </Reveal>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 border border-navy/10" data-testid="experience-grid">
            {experiences.map((e, i) => (
              <Reveal key={e.label} delay={i * 0.08} className="bg-white">
                <div className="p-8 flex flex-col gap-4 group hover:bg-slate-50 transition-colors duration-300 h-full" data-testid={`experience-item-${i}`}>
                  <e.icon size={28} className="text-brand-dark group-hover:-translate-y-1 transition-transform duration-300" />
                  <p className="font-head font-semibold text-navy text-sm md:text-base leading-snug">{e.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IN THE NEWS */}
      <section className="bg-slate-50 py-24 md:py-28 border-t border-navy/5" data-testid="news-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand-dark mb-4" data-testid="news-overline">In the News</p>
            <h2 className="font-display uppercase text-navy text-3xl md:text-5xl leading-tight mb-10" data-testid="news-heading">
              BuilderEDU on ABC7 Chicago
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative w-full aspect-video bg-navy-ink border border-navy/10 shadow-[0_24px_60px_-24px_rgba(14,21,46,0.35)] overflow-hidden" data-testid="news-video-frame">
              <iframe
                src="https://www.youtube-nocookie.com/embed/uc1iP1ocCbU"
                title="ABC7 Chicago: Suburban high school students help build houses in new Schaumburg subdivision"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                data-testid="news-video-iframe"
              />
            </div>
            <p className="mt-5 text-sm text-slate-500" data-testid="news-attribution">
              ABC7 Chicago · November 2024 ·{" "}
              <a
                href="https://abc7chicago.com/post/building-better-chicago-suburban-high-school-students-help-build-houses-new-subdivision-schaumburg/15543800/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-navy underline decoration-brand decoration-2 underline-offset-4 hover:text-brand-dark transition-colors"
                data-testid="news-article-link"
              >
                Read the full story
              </a>
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* PARTNERSHIP */}
      <section className="bg-slate-50 blueprint-grid-light py-24 md:py-32" data-testid="partnership-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand-dark mb-6" data-testid="partnership-overline">The Partnership</p>
            <h2 className="font-display uppercase text-navy text-3xl md:text-4xl leading-tight mb-6" data-testid="partnership-heading">
              Made possible through partnership
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-14" data-testid="partnership-copy">
              BuilderEDU is made possible through a partnership between High School District 211,
              The McKenzie Family Foundation, and Nitti Development.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-10" data-testid="partnership-logos">
              <div className="flex flex-col items-center gap-4">
                <div className="bg-white px-7 py-5 shadow-sm border border-navy/5 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <img src="/assets/district-211-logo.png" alt="High School District 211" className="h-16 md:h-20 w-auto" data-testid="district-211-logo" />
                </div>
                <p className="font-head text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500" data-testid="district-211-label">
                  School Partner <span className="text-brand-dark block mt-1">High School District 211</span>
                </p>
              </div>

              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-navy text-brand" data-testid="partnership-plus-1">
                <Plus size={16} strokeWidth={3} />
              </span>

              <div className="flex flex-col items-center gap-4">
                <div className="bg-white px-7 py-5 shadow-sm border border-navy/5 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <img src="/assets/mckenzie-foundation-logo.png" alt="The McKenzie Family Foundation" className="h-16 md:h-20 w-auto" data-testid="mckenzie-foundation-logo" />
                </div>
                <p className="font-head text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500" data-testid="mckenzie-foundation-label">
                  Foundation Partner <span className="text-brand-dark block mt-1">The McKenzie Family Foundation</span>
                </p>
              </div>

              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-navy text-brand" data-testid="partnership-plus-2">
                <Plus size={16} strokeWidth={3} />
              </span>

              <div className="flex flex-col items-center gap-4">
                <div className="bg-white px-7 py-5 shadow-sm border border-navy/5 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <img src="/assets/nitti-logo.png" alt="Nitti Development" className="h-16 md:h-20 w-auto" data-testid="nitti-logo" />
                </div>
                <p className="font-head text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500" data-testid="nitti-label">
                  Homebuilder <span className="text-brand-dark block mt-1">Nitti Development</span>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}
